import { GallerySku, SkuSizeMatch, SkuSizeNumeric } from 'domains/gallerySkuDomain';

/**
 * Rules for the gallery's ranking chips. Kept here rather than inline in a card so both gallery
 * views apply the same test — and so the null-versus-NONE distinction below is stated once.
 */

/**
 * Whether the SKU earned the "Matching dimensions" treatment.
 *
 * `sizeMatch` has three states and only one of them means "no match":
 * - `undefined` — the visit carried no readable design size, so the dimension tier never ran. Says
 *   nothing about this SKU.
 * - `'NONE'` — the tier ran and this SKU's sizes are outside tolerance.
 * - anything else — a match, exact or close, in the design's orientation or rotated.
 *
 * Testing for "not undefined" would tag every SKU on a visit with no design size, which is why
 * this checks the bands explicitly.
 */
export const isDimensionMatch = (sizeMatch?: SkuSizeMatch): boolean =>
  sizeMatch != null && sizeMatch !== 'NONE';

/**
 * Whether the dimension tier ran at all for this visit. Use it to decide whether size-related UI
 * (a "no products match your design's size" note, for instance) has anything to say.
 */
export const wasRankedByDimension = (sizeMatch?: SkuSizeMatch): boolean =>
  sizeMatch != null;

/**
 * The matched size as the catalogue stores it, e.g. `8.5x11`, so a chip can say which size matched
 * instead of only that one did. Undefined when nothing matched.
 */
export const matchedSizeLabel = (sku: GallerySku): string | undefined =>
  isDimensionMatch(sku.sizeMatch) ? closestSizeLabel(sku) : undefined;

/**
 * The nearest size the SKU offers, whether or not it is a match — the size the ranking put it
 * where it is. Not shown for a non-match: closeness is expressed by position, not by a label.
 */
export const closestSizeLabel = (sku: GallerySku): string | undefined => {
  const size = sku.closestSize?.size?.trim();
  return size ? size : undefined;
};

/**
 * The flat sheet the match was computed on, but only when it differs from the finished size.
 *
 * The API matches a design against the sheet the press prints, not against what the buyer
 * receives: a 3-panel gate fold finished at 9x6 is matched on its 9x12 flat. Naming only the 9x6
 * next to an A4 design makes a correct match look like a bug, which is exactly how it was
 * reported. Undefined for anything that neither folds nor binds, so the ordinary chip stays a
 * single size.
 */
export const matchedFlatSizeLabel = (sku: GallerySku): string | undefined => {
  if (!isDimensionMatch(sku.sizeMatch)) {
    return undefined;
  }
  const flat = sku.closestSize?.flatSize?.trim();
  const finished = sku.closestSize?.size?.trim();
  return flat && flat !== finished ? flat : undefined;
};

/**
 * Whether the SKU earned the "Most ordered" badge — the busiest one in the catalogue.
 *
 * Never computed from the items on screen: the server decides it over the whole result, so a page
 * that does not contain the winner correctly shows the badge on nobody.
 */
export const isMostOrdered = (sku: GallerySku): boolean => sku.mostOrdered === true;

/** True when the match only holds with width and length swapped, which is worth saying out loud. */
export const isRotatedMatch = (sizeMatch?: SkuSizeMatch): boolean =>
  sizeMatch === 'EXACT_ROTATED' || sizeMatch === 'CLOSE_ROTATED';

/**
 * Ranked position of a size DTO's dimensions as a display string, e.g. `8.5 x 11 in`. Falls back
 * to the raw catalogue string when the numbers are missing, so a malformed size still shows.
 */
export const formatSizeInInches = (size?: SkuSizeNumeric): string | undefined => {
  if (!size) {
    return undefined;
  }
  const { widthIn, lengthIn, heightIn } = size;
  if (widthIn == null || lengthIn == null) {
    return size.size?.trim() || undefined;
  }
  const dimensions =
    heightIn == null
      ? `${widthIn} x ${lengthIn}`
      : `${widthIn} x ${lengthIn} x ${heightIn}`;
  return `${dimensions} in`;
};
